import { AxiosRequestConfig, AxiosResponse, RawAxiosResponseHeaders, AxiosResponseHeaders } from 'axios';

import {
    authorizedAxiosInstance,
    authorizedStreamingAxiosInstance,
    unauthorizedAxiosInstance,
} from '@/clients/axios';

import { toApiServiceError } from './errors';

export type ApiClientMode = 'authorized' | 'streaming' | 'unauthorized';

export type ApiServiceResponse<TData> = {
    data: TData;
    headers: RawAxiosResponseHeaders | AxiosResponseHeaders;
    requestId?: string;
    status: number;
};

export type ApiRequestConfig<TBody = unknown, TParams = unknown> = Omit<
    AxiosRequestConfig<TBody>,
    'data' | 'method' | 'params' | 'url'
> & {
    data?: TBody;
    mode?: ApiClientMode;
    params?: TParams;
    suppressErrorCapture?: boolean;
};

function selectAxiosInstance(mode: ApiClientMode = 'authorized') {
    if (mode === 'unauthorized') {return unauthorizedAxiosInstance;}
    if (mode === 'streaming') {return authorizedStreamingAxiosInstance;}

    return authorizedAxiosInstance;
}

function toApiServiceResponse<TData>(response: AxiosResponse<TData>): ApiServiceResponse<TData> {
    const requestId = response.headers['x-request-id'] ?? response.headers['x-correlation-id'];

    return {
        data: response.data,
        headers: response.headers,
        requestId: Array.isArray(requestId) ? requestId[0] : requestId,
        status: response.status,
    };
}

async function requestResponse<TData = unknown, TBody = unknown, TParams = unknown>(
    method: NonNullable<AxiosRequestConfig['method']>,
    url: string,
    config: ApiRequestConfig<TBody, TParams> = {},
): Promise<ApiServiceResponse<TData>> {
    const { data, mode, params, ...axiosConfig } = config;

    try {
        const response = await selectAxiosInstance(mode).request<TData>({
            ...axiosConfig,
            data,
            method,
            params,
            url,
        });

        return toApiServiceResponse(response);
    } catch (error) {
        throw toApiServiceError(error);
    }
}

async function requestData<TData = unknown, TBody = unknown, TParams = unknown>(
    method: NonNullable<AxiosRequestConfig['method']>,
    url: string,
    config: ApiRequestConfig<TBody, TParams> = {},
): Promise<TData> {
    const response = await requestResponse<TData, TBody, TParams>(method, url, config);

    return response.data;
}

export const apiClient = {
    delete: <TData = void, TParams = unknown>(url: string, config?: ApiRequestConfig<unknown, TParams>) =>
        requestData<TData, unknown, TParams>('delete', url, config),
    get: <TData = unknown, TParams = unknown>(url: string, config?: ApiRequestConfig<unknown, TParams>) =>
        requestData<TData, unknown, TParams>('get', url, config),
    patch: <TData = unknown, TBody = unknown, TParams = unknown>(
        url: string,
        data?: TBody,
        config?: ApiRequestConfig<TBody, TParams>,
    ) => requestData<TData, TBody, TParams>('patch', url, { ...config, data }),
    post: <TData = unknown, TBody = unknown, TParams = unknown>(
        url: string,
        data?: TBody,
        config?: ApiRequestConfig<TBody, TParams>,
    ) => requestData<TData, TBody, TParams>('post', url, { ...config, data }),
    put: <TData = unknown, TBody = unknown, TParams = unknown>(
        url: string,
        data?: TBody,
        config?: ApiRequestConfig<TBody, TParams>,
    ) => requestData<TData, TBody, TParams>('put', url, { ...config, data }),
    response: {
        delete: <TData = void, TParams = unknown>(url: string, config?: ApiRequestConfig<unknown, TParams>) =>
            requestResponse<TData, unknown, TParams>('delete', url, config),
        get: <TData = unknown, TParams = unknown>(url: string, config?: ApiRequestConfig<unknown, TParams>) =>
            requestResponse<TData, unknown, TParams>('get', url, config),
        patch: <TData = unknown, TBody = unknown, TParams = unknown>(
            url: string,
            data?: TBody,
            config?: ApiRequestConfig<TBody, TParams>,
        ) => requestResponse<TData, TBody, TParams>('patch', url, { ...config, data }),
        post: <TData = unknown, TBody = unknown, TParams = unknown>(
            url: string,
            data?: TBody,
            config?: ApiRequestConfig<TBody, TParams>,
        ) => requestResponse<TData, TBody, TParams>('post', url, { ...config, data }),
        put: <TData = unknown, TBody = unknown, TParams = unknown>(
            url: string,
            data?: TBody,
            config?: ApiRequestConfig<TBody, TParams>,
        ) => requestResponse<TData, TBody, TParams>('put', url, { ...config, data }),
    },
};
